import { useId } from "react";

import { FunnelIcon } from "@phosphor-icons/react";
import {
  Box,
  Button,
  Checkbox,
  DropdownMenu,
  Flex,
  Heading,
  IconButton,
  Popover,
  SegmentedControl,
  Select,
  Slider,
  Spinner,
  Text,
} from "@radix-ui/themes";

import { ColumnsPopover } from "~/v3-poc/components/ColumnsPopover";
import type {
  PositionsWidgetColumnId,
  PositionsWidgetColumnOption,
} from "./PositionsWidget.columns";
import type {
  PositionsWidgetAssetClassFilter,
  PositionsWidgetColumnPreset,
  PositionsWidgetFilters,
  PositionsWidgetGrouping,
  PositionsWidgetPositionStateFilter,
  PositionsWidgetRiskPreset,
  PositionsWidgetSourceFilter,
} from "./PositionsWidget.types";
import { getActivePositionsFilterCount } from "./PositionsWidget.utils";
import { SymbolSearchField } from "./SymbolSearchField";
import type { PositionsTableDensity } from "./usePositionsTableDisplay";

const ICON_SIZE = 14;

const ASSET_CLASS_OPTIONS: {
  label: string;
  value: PositionsWidgetAssetClassFilter;
}[] = [
  { label: "All asset classes", value: "all" },
  { label: "Equity positions", value: "us_equity" },
  { label: "Option positions", value: "us_option" },
  { label: "Crypto positions", value: "crypto" },
];

const ASSET_CLASS_TABS = [
  { label: "All", value: "all" },
  { label: "Equity", value: "us_equity" },
  { label: "Options", value: "us_option" },
  { label: "Crypto", value: "crypto" },
] as const satisfies readonly {
  label: string;
  value: PositionsWidgetAssetClassFilter;
}[];

const DENSITY_OPTIONS: {
  label: string;
  value: PositionsTableDensity;
}[] = [
  { label: "Compact", value: "condensed" },
  { label: "Default", value: "normal" },
  { label: "Comfortable", value: "expanded" },
];

const isAssetClassTab = (
  value: string,
): value is (typeof ASSET_CLASS_TABS)[number]["value"] =>
  ASSET_CLASS_TABS.some((tab) => tab.value === value);

const DTE_OPTIONS: { label: string; value: string }[] = [
  { label: "All expirations", value: "all" },
  { label: "<= 1 day", value: "1" },
  { label: "<= 7 days", value: "7" },
  { label: "<= 10 days", value: "10" },
  { label: "<= 30 days", value: "30" },
];

const POSITION_STATE_OPTIONS: {
  label: string;
  value: PositionsWidgetPositionStateFilter;
}[] = [
  { label: "All statuses", value: "all" },
  { label: "Open positions", value: "open" },
  { label: "Closed positions", value: "closed" },
  { label: "Filled orders", value: "fill" },
];

const GROUP_BY_OPTIONS: { label: string; value: PositionsWidgetGrouping }[] = [
  { label: "Group by: Sub-account", value: "subAccount" },
  { label: "Group by: Underlying", value: "underlying" },
  { label: "Group by: None", value: "flat" },
  { label: "Group by: Asset class", value: "assetClass" },
];

const SOURCE_OPTIONS: { label: string; value: PositionsWidgetSourceFilter }[] =
  [
    { label: "All sources", value: "all" },
    { label: "API", value: "api" },
    { label: "Dashboard", value: "dashboard" },
    { label: "API · Agent", value: "agent" },
  ];

const RISK_PRESET_OPTIONS: {
  label: string;
  value: PositionsWidgetRiskPreset;
}[] = [
  { label: "Risk presets", value: "none" },
  { label: "Worst P&L today", value: "worst-pnl" },
  { label: "Largest % book", value: "largest-weight" },
  { label: "Expiring soon", value: "expiring-soon" },
];

const COLUMN_PRESET_OPTIONS: {
  label: string;
  value: PositionsWidgetColumnPreset;
}[] = [
  { label: "Supervision", value: "default" },
  { label: "Risk intervention", value: "risk" },
  { label: "Manual trading", value: "manual" },
  { label: "Agent review", value: "agent" },
];

export interface PositionsToolbarPresentationProps {
  surface?: "standalone" | "workspace";
  scopeSymbol?: string;
  filters: PositionsWidgetFilters;
  onFiltersChange: (filters: PositionsWidgetFilters) => void;
  columnOptions: readonly PositionsWidgetColumnOption[];
  visibleColumnIds: readonly PositionsWidgetColumnId[];
  requiredVisibleColumnIds?: readonly PositionsWidgetColumnId[];
  onColumnVisibilityChange: (
    columnId: PositionsWidgetColumnId,
    isVisible: boolean,
  ) => void;
  onSelectAllColumns: () => void;
  onResetColumns: () => void;
  density?: PositionsTableDensity;
  horizontalLines?: boolean;
  verticalLines?: boolean;
  onDensityChange?: (density: PositionsTableDensity) => void;
  onHorizontalLinesChange?: (enabled: boolean) => void;
  onVerticalLinesChange?: (enabled: boolean) => void;
  openCount: number;
  selectedCount: number;
  bulkCloseRatio: number;
  onBulkCloseRatioChange: (ratio: number) => void;
  onClearSelected: () => void;
  onRequestCloseInRatio: (closeRatioPct: number) => void;
  onLiquidateOpenChange: (open: boolean) => void;
  isLoading: boolean;
  isFetching: boolean;
}

const ToolbarSelect = <T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly { label: string; value: T }[];
  onChange: (value: T) => void;
}) => (
  <Box className="min-w-0 max-w-full flex-1 basis-[9rem]">
    <Select.Root
      size="2"
      value={value}
      onValueChange={(next) => {
        const match = options.find((option) => option.value === next);
        if (match) {
          onChange(match.value);
        }
      }}
    >
      <Select.Trigger aria-label={label} className="h-8 w-full" />
      <Select.Content>
        {options.map((option) => (
          <Select.Item key={option.value} value={option.value}>
            {option.label}
          </Select.Item>
        ))}
      </Select.Content>
    </Select.Root>
  </Box>
);

const ToolbarCheckbox = ({
  label,
  checked,
  onCheckedChange,
}: {
  label: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}) => {
  const id = useId();
  return (
    <Flex asChild align="center" gap="1" className="min-w-0 shrink-0">
      <label htmlFor={id}>
        <Checkbox
          id={id}
          size="1"
          checked={checked}
          onCheckedChange={(next) => onCheckedChange(next === true)}
        />
        <Text size="1">{label}</Text>
      </label>
    </Flex>
  );
};

const PositionsColumnsPopover = ({
  columnOptions,
  visibleColumnIds,
  requiredVisibleColumnIds,
  onColumnVisibilityChange,
  onSelectAllColumns,
  onResetColumns,
}: {
  columnOptions: readonly PositionsWidgetColumnOption[];
  visibleColumnIds: readonly PositionsWidgetColumnId[];
  requiredVisibleColumnIds: readonly PositionsWidgetColumnId[];
  onColumnVisibilityChange: (
    columnId: PositionsWidgetColumnId,
    isVisible: boolean,
  ) => void;
  onSelectAllColumns: () => void;
  onResetColumns: () => void;
}) => {
  const visibleSet = new Set(visibleColumnIds);
  const requiredSet = new Set(requiredVisibleColumnIds);
  const pickerOptions = columnOptions.filter(
    (option) => option.id !== "closeQty" && option.id !== "closeRatio",
  );
  const hideableCount = pickerOptions.filter(
    (option) => !requiredSet.has(option.id),
  ).length;
  const visibleHideableCount = pickerOptions.filter(
    (option) => !requiredSet.has(option.id) && visibleSet.has(option.id),
  ).length;

  return (
    <ColumnsPopover
      triggerVariant="icon"
      visibleCount={visibleHideableCount}
      totalCount={hideableCount}
      onSelectAll={onSelectAllColumns}
      onReset={onResetColumns}
    >
      {pickerOptions.map((option) => {
        const checkboxId = `positions-column-${option.id}`;
        const checked = visibleSet.has(option.id);
        const locked =
          requiredSet.has(option.id) ||
          (checked && visibleHideableCount <= 1 && !requiredSet.has(option.id));
        return (
          <Flex
            key={option.id}
            asChild
            align="center"
            justify="between"
            gap="3"
          >
            <label htmlFor={checkboxId}>
              <Checkbox
                id={checkboxId}
                size="1"
                checked={checked}
                disabled={locked}
                onCheckedChange={(next) =>
                  onColumnVisibilityChange(option.id, next === true)
                }
              />
              <Text size="2">{option.label}</Text>
            </label>
          </Flex>
        );
      })}
    </ColumnsPopover>
  );
};

const OverflowMenu = ({
  density,
  horizontalLines,
  verticalLines,
  onDensityChange,
  onHorizontalLinesChange,
  onVerticalLinesChange,
}: {
  density: PositionsTableDensity;
  horizontalLines: boolean;
  verticalLines: boolean;
  onDensityChange: (density: PositionsTableDensity) => void;
  onHorizontalLinesChange: (enabled: boolean) => void;
  onVerticalLinesChange: (enabled: boolean) => void;
}) => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger>
      <Button
        type="button"
        size="1"
        variant="ghost"
        color="gray"
        aria-label="Table display options"
      >
        Display
      </Button>
    </DropdownMenu.Trigger>
    <DropdownMenu.Content size="1" align="end">
      <DropdownMenu.Label>Row spacing</DropdownMenu.Label>
      <DropdownMenu.RadioGroup
        value={density}
        onValueChange={(value) => {
          if (
            value === "condensed" ||
            value === "normal" ||
            value === "expanded"
          ) {
            onDensityChange(value);
          }
        }}
      >
        {DENSITY_OPTIONS.map((option) => (
          <DropdownMenu.RadioItem key={option.value} value={option.value}>
            {option.label}
          </DropdownMenu.RadioItem>
        ))}
      </DropdownMenu.RadioGroup>
      <DropdownMenu.Separator />
      <DropdownMenu.CheckboxItem
        checked={horizontalLines}
        onSelect={(event) => event.preventDefault()}
        onCheckedChange={(checked) => onHorizontalLinesChange(checked === true)}
      >
        Horizontal lines
      </DropdownMenu.CheckboxItem>
      <DropdownMenu.CheckboxItem
        checked={verticalLines}
        onSelect={(event) => event.preventDefault()}
        onCheckedChange={(checked) => onVerticalLinesChange(checked === true)}
      >
        Vertical lines
      </DropdownMenu.CheckboxItem>
    </DropdownMenu.Content>
  </DropdownMenu.Root>
);

/**
 * Selection close-out, copied from positions-next: count, ratio slider, Clear,
 * and Close N. Inserts to the left of Liquidate all so that button stays put.
 */
const BulkCloseActions = ({
  selectedCount,
  closeRatio,
  onCloseRatioChange,
  onClear,
  onClose,
}: {
  selectedCount: number;
  closeRatio: number;
  onCloseRatioChange: (ratio: number) => void;
  onClear: () => void;
  onClose: (closeRatioPct: number) => void;
}) => {
  if (selectedCount === 0) {
    return null;
  }
  return (
    <Flex align="center" gap="3" wrap="wrap">
      <Text size="1" weight="medium">
        {selectedCount} selected
      </Text>
      <Flex align="center" gap="2">
        <Text size="1" color="gray">
          Close ratio
        </Text>
        <Slider
          size="1"
          min={1}
          max={100}
          step={1}
          value={[closeRatio]}
          aria-label="Close ratio"
          className="w-[110px]"
          onValueChange={([next]) => {
            if (typeof next === "number") {
              onCloseRatioChange(next);
            }
          }}
        />
        <Text size="1" className="atd-numeric min-w-[4ch] whitespace-nowrap">
          {closeRatio}%
        </Text>
      </Flex>
      <Button
        type="button"
        size="1"
        variant="soft"
        color="gray"
        onClick={onClear}
      >
        Clear
      </Button>
      <Button
        type="button"
        size="1"
        color="red"
        onClick={() => onClose(closeRatio)}
      >
        Close {selectedCount}
      </Button>
    </Flex>
  );
};

const getPositionsFunnelFilterCount = (
  filters: PositionsWidgetFilters,
): number =>
  getActivePositionsFilterCount(filters) - Number(filters.assetClass !== "all");

/**
 * Second widget row (positions-next): filters left, liquidate/selection right.
 * The workspace title row keeps the drag handle and widget ⋮ menu only.
 */
const PositionsHeaderChrome = ({
  filters,
  onFiltersChange,
  columnOptions,
  visibleColumnIds,
  requiredVisibleColumnIds,
  onColumnVisibilityChange,
  onSelectAllColumns,
  onResetColumns,
  density,
  horizontalLines,
  verticalLines,
  onDensityChange,
  onHorizontalLinesChange,
  onVerticalLinesChange,
  openCount,
  selectedCount,
  bulkCloseRatio,
  onBulkCloseRatioChange,
  onLiquidateOpenChange,
  onClearSelected,
  onRequestCloseInRatio,
  isFetching,
}: {
  filters: PositionsWidgetFilters;
  onFiltersChange: (next: PositionsWidgetFilters) => void;
  columnOptions: readonly PositionsWidgetColumnOption[];
  visibleColumnIds: readonly PositionsWidgetColumnId[];
  requiredVisibleColumnIds: readonly PositionsWidgetColumnId[];
  onColumnVisibilityChange: (
    columnId: PositionsWidgetColumnId,
    isVisible: boolean,
  ) => void;
  onSelectAllColumns: () => void;
  onResetColumns: () => void;
  density: PositionsTableDensity;
  horizontalLines: boolean;
  verticalLines: boolean;
  onDensityChange: (density: PositionsTableDensity) => void;
  onHorizontalLinesChange: (enabled: boolean) => void;
  onVerticalLinesChange: (enabled: boolean) => void;
  openCount: number;
  selectedCount: number;
  bulkCloseRatio: number;
  onBulkCloseRatioChange: (ratio: number) => void;
  onLiquidateOpenChange: (open: boolean) => void;
  onClearSelected: () => void;
  onRequestCloseInRatio: (closeRatioPct: number) => void;
  isFetching: boolean;
}) => {
  const funnelCount = getPositionsFunnelFilterCount(filters);
  const tabValue = isAssetClassTab(filters.assetClass)
    ? filters.assetClass
    : undefined;

  return (
    <Flex
      align="center"
      justify="between"
      gap="3"
      wrap="wrap"
      px="2"
      py="2"
      flexShrink="0"
      className="border-[color:var(--gray-a5)] border-b"
      data-testid="positions-filter-row"
    >
      <Flex align="center" gap="2" wrap="wrap">
        <SymbolSearchField
          value={filters.search}
          onChange={(search) => onFiltersChange({ ...filters, search })}
          onClear={() => onFiltersChange({ ...filters, search: "" })}
        />
        <SegmentedControl.Root
          size="1"
          value={tabValue}
          aria-label="Asset class"
          onValueChange={(value) => {
            if (isAssetClassTab(value)) {
              onFiltersChange({ ...filters, assetClass: value });
            }
          }}
        >
          {ASSET_CLASS_TABS.map((tab) => (
            <SegmentedControl.Item key={tab.value} value={tab.value}>
              {tab.label}
            </SegmentedControl.Item>
          ))}
        </SegmentedControl.Root>
        <PositionsColumnsPopover
          columnOptions={columnOptions}
          visibleColumnIds={visibleColumnIds}
          requiredVisibleColumnIds={requiredVisibleColumnIds}
          onColumnVisibilityChange={onColumnVisibilityChange}
          onSelectAllColumns={onSelectAllColumns}
          onResetColumns={onResetColumns}
        />
        <Popover.Root>
          <Popover.Trigger>
            <IconButton
              type="button"
              size="1"
              variant="ghost"
              color="gray"
              aria-label={
                funnelCount > 0 ? `Filters, ${funnelCount} active` : "Filters"
              }
            >
              <FunnelIcon size={ICON_SIZE} aria-hidden />
            </IconButton>
          </Popover.Trigger>
          <Popover.Content width="360px" maxWidth="calc(100vw - 2rem)">
            <Flex direction="column" gap="3">
              <Heading as="h3" size="2" weight="medium">
                Filters
              </Heading>
              <Flex wrap="wrap" align="center" gap="2">
                <ToolbarSelect
                  label="Asset class"
                  value={filters.assetClass}
                  options={ASSET_CLASS_OPTIONS}
                  onChange={(assetClass) =>
                    onFiltersChange({ ...filters, assetClass })
                  }
                />
                <ToolbarSelect
                  label="Days to expiration"
                  value={filters.maxDaysToExpiry}
                  options={DTE_OPTIONS}
                  onChange={(maxDaysToExpiry) =>
                    onFiltersChange({ ...filters, maxDaysToExpiry })
                  }
                />
                <ToolbarSelect
                  label="Position state"
                  value={filters.positionState}
                  options={POSITION_STATE_OPTIONS}
                  onChange={(positionState) =>
                    onFiltersChange({ ...filters, positionState })
                  }
                />
                <ToolbarSelect
                  label="Source"
                  value={filters.source}
                  options={SOURCE_OPTIONS}
                  onChange={(source) => onFiltersChange({ ...filters, source })}
                />
                <ToolbarSelect
                  label="Risk preset"
                  value={filters.riskPreset}
                  options={RISK_PRESET_OPTIONS}
                  onChange={(riskPreset) =>
                    onFiltersChange({ ...filters, riskPreset })
                  }
                />
                <ToolbarSelect
                  label="Column preset"
                  value={filters.columnPreset}
                  options={COLUMN_PRESET_OPTIONS}
                  onChange={(columnPreset) =>
                    onFiltersChange({ ...filters, columnPreset })
                  }
                />
                <ToolbarSelect
                  label="Group by"
                  value={filters.grouping}
                  options={GROUP_BY_OPTIONS}
                  onChange={(grouping) =>
                    onFiltersChange({ ...filters, grouping })
                  }
                />
                <ToolbarCheckbox
                  label="Watchlist only"
                  checked={filters.watchlistOnly}
                  onCheckedChange={(watchlistOnly) =>
                    onFiltersChange({ ...filters, watchlistOnly })
                  }
                />
                <ToolbarCheckbox
                  label="Flagged only"
                  checked={filters.flaggedOnly}
                  onCheckedChange={(flaggedOnly) =>
                    onFiltersChange({ ...filters, flaggedOnly })
                  }
                />
                <ToolbarCheckbox
                  label="Staged only"
                  checked={filters.stagedOnly}
                  onCheckedChange={(stagedOnly) =>
                    onFiltersChange({ ...filters, stagedOnly })
                  }
                />
              </Flex>
              <OverflowMenu
                density={density}
                horizontalLines={horizontalLines}
                verticalLines={verticalLines}
                onDensityChange={onDensityChange}
                onHorizontalLinesChange={onHorizontalLinesChange}
                onVerticalLinesChange={onVerticalLinesChange}
              />
            </Flex>
          </Popover.Content>
        </Popover.Root>
        {isFetching ? <Spinner size="1" /> : null}
      </Flex>
      <Flex align="center" gap="3" wrap="nowrap">
        <BulkCloseActions
          selectedCount={selectedCount}
          closeRatio={bulkCloseRatio}
          onCloseRatioChange={onBulkCloseRatioChange}
          onClear={onClearSelected}
          onClose={onRequestCloseInRatio}
        />
        <Button
          type="button"
          size="1"
          variant="soft"
          color="red"
          className="shrink-0"
          disabled={openCount === 0}
          onClick={() => onLiquidateOpenChange(true)}
        >
          Liquidate all
        </Button>
      </Flex>
    </Flex>
  );
};

export const PositionsToolbarPresentation = ({
  surface = "standalone",
  scopeSymbol,
  filters,
  onFiltersChange,
  columnOptions,
  visibleColumnIds,
  requiredVisibleColumnIds = [],
  onColumnVisibilityChange,
  onSelectAllColumns,
  onResetColumns,
  density = "normal",
  horizontalLines = true,
  verticalLines = false,
  onDensityChange = () => undefined,
  onHorizontalLinesChange = () => undefined,
  onVerticalLinesChange = () => undefined,
  openCount,
  selectedCount,
  bulkCloseRatio,
  onBulkCloseRatioChange,
  onClearSelected,
  onRequestCloseInRatio,
  onLiquidateOpenChange,
  isLoading,
  isFetching,
}: PositionsToolbarPresentationProps) => {
  const isWorkspace = surface === "workspace";
  const showFetchingSpinner = isFetching && !isLoading;

  if (isWorkspace) {
    return (
      <PositionsHeaderChrome
        filters={filters}
        onFiltersChange={onFiltersChange}
        columnOptions={columnOptions}
        visibleColumnIds={visibleColumnIds}
        requiredVisibleColumnIds={requiredVisibleColumnIds}
        onColumnVisibilityChange={onColumnVisibilityChange}
        onSelectAllColumns={onSelectAllColumns}
        onResetColumns={onResetColumns}
        density={density}
        horizontalLines={horizontalLines}
        verticalLines={verticalLines}
        onDensityChange={onDensityChange}
        onHorizontalLinesChange={onHorizontalLinesChange}
        onVerticalLinesChange={onVerticalLinesChange}
        openCount={openCount}
        selectedCount={selectedCount}
        bulkCloseRatio={bulkCloseRatio}
        onBulkCloseRatioChange={onBulkCloseRatioChange}
        onLiquidateOpenChange={onLiquidateOpenChange}
        onClearSelected={onClearSelected}
        onRequestCloseInRatio={onRequestCloseInRatio}
        isFetching={showFetchingSpinner}
      />
    );
  }

  return (
    <Flex
      direction="column"
      gap="2"
      flexShrink="0"
      className="min-w-0"
      data-testid="positions-toolbar"
    >
      <Flex wrap="wrap" align="center" gap="2" className="min-w-0">
        <Text size="1" weight="medium" color="gray" className="shrink-0">
          {scopeSymbol ? `Positions · ${scopeSymbol}` : "Positions"}
        </Text>
        <ToolbarSelect
          label="Asset class"
          value={filters.assetClass}
          options={ASSET_CLASS_OPTIONS}
          onChange={(assetClass) => onFiltersChange({ ...filters, assetClass })}
        />
        <ToolbarSelect
          label="Days to expiration"
          value={filters.maxDaysToExpiry}
          options={DTE_OPTIONS}
          onChange={(maxDaysToExpiry) =>
            onFiltersChange({ ...filters, maxDaysToExpiry })
          }
        />
        <ToolbarSelect
          label="Position state"
          value={filters.positionState}
          options={POSITION_STATE_OPTIONS}
          onChange={(positionState) =>
            onFiltersChange({ ...filters, positionState })
          }
        />
        <ToolbarSelect
          label="Source"
          value={filters.source}
          options={SOURCE_OPTIONS}
          onChange={(source) => onFiltersChange({ ...filters, source })}
        />
        <ToolbarSelect
          label="Risk preset"
          value={filters.riskPreset}
          options={RISK_PRESET_OPTIONS}
          onChange={(riskPreset) => onFiltersChange({ ...filters, riskPreset })}
        />
        <ToolbarSelect
          label="Column preset"
          value={filters.columnPreset}
          options={COLUMN_PRESET_OPTIONS}
          onChange={(columnPreset) =>
            onFiltersChange({ ...filters, columnPreset })
          }
        />
        <ToolbarSelect
          label="Group by"
          value={filters.grouping}
          options={GROUP_BY_OPTIONS}
          onChange={(grouping) => onFiltersChange({ ...filters, grouping })}
        />
        <ToolbarCheckbox
          label="Watchlist only"
          checked={filters.watchlistOnly}
          onCheckedChange={(watchlistOnly) =>
            onFiltersChange({ ...filters, watchlistOnly })
          }
        />
        <ToolbarCheckbox
          label="Flagged only"
          checked={filters.flaggedOnly}
          onCheckedChange={(flaggedOnly) =>
            onFiltersChange({ ...filters, flaggedOnly })
          }
        />
        <ToolbarCheckbox
          label="Staged only"
          checked={filters.stagedOnly}
          onCheckedChange={(stagedOnly) =>
            onFiltersChange({ ...filters, stagedOnly })
          }
        />
      </Flex>
      <Flex
        align="center"
        justify="end"
        gap="3"
        wrap="wrap"
        className="w-full min-w-0"
      >
        <SymbolSearchField
          value={filters.search}
          onChange={(search) => onFiltersChange({ ...filters, search })}
          onClear={() => onFiltersChange({ ...filters, search: "" })}
        />
        <PositionsColumnsPopover
          columnOptions={columnOptions}
          visibleColumnIds={visibleColumnIds}
          requiredVisibleColumnIds={requiredVisibleColumnIds}
          onColumnVisibilityChange={onColumnVisibilityChange}
          onSelectAllColumns={onSelectAllColumns}
          onResetColumns={onResetColumns}
        />
        {showFetchingSpinner ? <Spinner size="1" /> : null}
        <OverflowMenu
          density={density}
          horizontalLines={horizontalLines}
          verticalLines={verticalLines}
          onDensityChange={onDensityChange}
          onHorizontalLinesChange={onHorizontalLinesChange}
          onVerticalLinesChange={onVerticalLinesChange}
        />
        <Flex align="center" gap="3" wrap="nowrap">
          <BulkCloseActions
            selectedCount={selectedCount}
            closeRatio={bulkCloseRatio}
            onCloseRatioChange={onBulkCloseRatioChange}
            onClear={onClearSelected}
            onClose={onRequestCloseInRatio}
          />
          <Button
            type="button"
            size="1"
            variant="soft"
            color="red"
            disabled={openCount === 0}
            onClick={() => onLiquidateOpenChange(true)}
          >
            Liquidate all
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
